import { Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";

import { Button, Card, PageSection, StatusMessage } from "@ceedero/ui";

import { AccountAvatar } from "@/account/components/account-avatar";
import { useRemoveAvatar, useUploadAvatar, type AccountProfile } from "@/account/hooks/use-account";
import { resolveAvatarErrorKey } from "@/account/utils/account-refusal";
import { AVATAR_FILE_ACCEPT, validateAvatarFile } from "@/account/utils/avatar";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * The account's picture.
 *
 * A hidden input driven by a button rather than a bare file input, because the
 * native control cannot be styled and announces itself in the operating
 * system's language over a page drawn in the user's chosen one.
 *
 * The file is checked before it is sent. The API checks it again and its answer
 * is the authoritative one, but a round trip to be told "that will not do" is a
 * round trip the user waited through for a fact their own machine already knew.
 */
export const AvatarSection = ({ profile }: { profile: AccountProfile }) => {
  const { t } = useTranslation();
  const uploadAvatar = useUploadAvatar();
  const removeAvatar = useRemoveAvatar();
  const [fileError, setFileError] = useState<TranslationKey | undefined>(undefined);
  const fileInput = useRef<HTMLInputElement>(null);

  const refusalKey =
    resolveAvatarErrorKey(uploadAvatar.error) ?? resolveAvatarErrorKey(removeAvatar.error);

  const chooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset first, so choosing the same file twice after a failure fires the
    // change event the second time as well.
    event.target.value = "";
    if (!file) {
      return;
    }
    const error = validateAvatarFile(file);
    setFileError(error);
    if (error !== undefined) {
      return;
    }
    uploadAvatar.reset();
    removeAvatar.reset();
    uploadAvatar.mutate(file);
  };

  return (
    <PageSection title={t("account.avatar.title")} description={t("account.avatar.description")}>
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-4">
          <AccountAvatar profile={profile} className="size-16 rounded-full" />

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept={AVATAR_FILE_ACCEPT}
              onChange={chooseFile}
              className="sr-only"
              tabIndex={-1}
              aria-hidden
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInput.current?.click()}
              isLoading={uploadAvatar.isPending}
            >
              <Upload />
              {t("account.avatar.choose")}
            </Button>
            {/*
              Absent rather than disabled when there is no picture: there is
              nothing a press could remove, and no action the user could take
              that would bring the control to life.
            */}
            {profile.avatarRevision != null && (
              <Button
                type="button"
                variant="destructiveGhost"
                onClick={() => {
                  setFileError(undefined);
                  uploadAvatar.reset();
                  removeAvatar.mutate();
                }}
                isLoading={removeAvatar.isPending}
              >
                <Trash2 />
                {t("account.avatar.remove")}
              </Button>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">{t("account.avatar.hint")}</p>

        {fileError !== undefined && <StatusMessage tone="error">{t(fileError)}</StatusMessage>}
        {refusalKey !== undefined && <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>}
        {uploadAvatar.isSuccess && (
          <StatusMessage tone="success">{t("account.avatar.saved")}</StatusMessage>
        )}
      </Card>
    </PageSection>
  );
};
